/* eslint-disable import/no-named-as-default-member */
/* eslint-disable @typescript-eslint/no-explicit-any */
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import { useRollbar } from '@rollbar/react'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import Page from './elements/Page'
import EmptyState from './EmptyState'
import PaymongoCheckoutForm from './PaymongoCheckoutForm'
import RegistrationSummary from './RegistrationSummary'
import { PAYMENT_MODES, TEAM_TYPES } from '../constants/models'
import { EventWaiver } from '../constants/types'
import { CartContext } from '../contexts/CartContext'
import { PageLoaderContext } from '../contexts/PageLoaderContext'
import useCategoryFees from '../hooks/useCategoryFees'
import {
  PaymentIntentResponse,
  AttachPaymentIntentResponse,
} from '../models/Paymongo/PaymentIntent'
import { PaymentMethodResponse } from '../models/Paymongo/PaymentMethod'
import { generateCartItems } from '../utils/ApiHelpers'
import {
  createPaymentIntent,
  createPaymentMethod,
  attachToPaymentIntent,
  doCreatePendingOrder,
  doCreateFreeRegistration,
  fetchCategoryFees,
} from '../utils/PaymongoCheckoutUtils'

interface Props {
  show?: boolean
  onCancel?: React.MouseEventHandler<HTMLButtonElement>
  isVirtualEvent?: boolean
  eventWaiver?: EventWaiver | null
  isFree?: boolean
}

interface OngoingPaymentNoticeProps {
  isFree?: boolean
}
const OngoingPaymentNotice: React.FC<OngoingPaymentNoticeProps> = (isFree) => (
  <div
    style={{
      textAlign: 'center',
      padding: '19px',
      marginBottom: '30px',
      border: '2px solid red',
      color: 'black',
    }}>
    Please <b>DO NOT refresh</b> the page and wait while we are processing your
    {isFree ? ' submission' : ' payment'}.
  </div>
)

const PaymongoCheckout: React.FC<Props> = ({
  isVirtualEvent = false,
  eventWaiver = null,
  isFree = false,
  show = true,
  onCancel,
}) => {
  const rollbar = useRollbar()

  const { enqueueSnackbar } = useSnackbar()

  const [checkWaiver, setCheckWaiver] = useState(false)
  const [checkWaiverOrg, setCheckWaiverOrg] = useState(eventWaiver === null)
  const [checkAgreeVerify, setCheckAgreeVerify] = useState(isVirtualEvent)
  const { setLoading: setPageLoading, pageLoading } =
    useContext(PageLoaderContext)

  const [loading, setLoading] = useState(false)
  // const [shippingPreference, setShippingPreference] = useState('CLAIM')
  const shippingPreference = 'CLAIM'

  const [shoppingCartItems, setShoppingCartItems] = useState([])
  const [shoppingCartTotal, setShoppingCartTotal] = useState(0)

  const { cartItems, cartUser } = useContext(CartContext)

  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.CARD.key)

  const categoryIds = useMemo(
    () => cartItems.map((item) => item.categoryId),
    [cartItems],
  )

  const displayError = (message: string) => {
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 })
  }

  const { categories, apiLoading } = useCategoryFees({
    categoryIds,
    // onError: displayError,
  })

  const updateShoppingItems = useCallback(() => {
    const { items, total } = generateCartItems(
      cartItems,
      categories,
      paymentMode,
    )
    setShoppingCartItems(items)
    setShoppingCartTotal(total)
  }, [categories, cartItems, paymentMode])

  useEffect(() => {
    updateShoppingItems()
    setPageLoading(apiLoading)

    return () => {
      setPageLoading(false)
    }
  }, [updateShoppingItems, setPageLoading, apiLoading])

  const handleCheck = (e) => {
    if (e.target.name === 'waiverCB') setCheckWaiver(e.target.checked)
    if (e.target.name === 'waiverOrg') setCheckWaiverOrg(e.target.checked)
    if (e.target.name === 'agreeVerify') setCheckAgreeVerify(e.target.checked)
  }

  const errorCallback = (error: any) => {
    rollbar.critical(error)
    if (typeof error === 'object' && 'message' in error)
      displayError(error.message)
    else displayError(error)
  }

  const handleFreeSubmit = async () => {
    rollbar.info({
      cartUser,
      cartItems,
    })

    setLoading(true)
    setPageLoading(true)
    try {
      await doCreateFreeRegistration(cartUser, cartItems)
      setLoading(false)
      setPageLoading(false)
      window.location.href = `${window.location.origin}${process.env.REACT_APP_PAYMONGO_RETURN_URL}?free_registration`
    } catch (e) {
      setLoading(false)
      setPageLoading(false)
      errorCallback(e)
    }
  }

  const renderCheckout = () => {
    return (
      <>
        <PaymongoCheckoutForm
          shoppingCartTotal={shoppingCartTotal}
          setPaymentMode={setPaymentMode}
          initialValues={{
            paymentType: paymentMode,
            country: 'PH',
            privacyPolicy: false,
          }}
          displayError={(values) => displayError(`${values.message}`)}
          updateData={async (values) => {
            rollbar.info({
              cartUser,
              cartItems,
            })

            // server: call paymongo api to Create a PaymentIntent. This should return clientId
            try {
              let currentCategoryFees = await fetchCategoryFees(categoryIds)
              if (currentCategoryFees.length) currentCategoryFees = categories

              const { items: newCartItems, total: newShoppingCartTotal } =
                generateCartItems(cartItems, currentCategoryFees, paymentMode)

              // check registrationCutOff
              if (
                dayjs().isSameOrAfter(
                  dayjs(
                    currentCategoryFees[newCartItems[0].categoryId]
                      .registrationCutOff,
                  ),
                )
              )
                return [
                  null,
                  {
                    title: 'Event unavailable',
                    message: 'Sorry. Event registration has ended.',
                  },
                ]

              const metadata = {}
              newCartItems.forEach((item, idx) => {
                metadata[
                  `itemName${idx + 1}`
                ] = `${item.eventName} [${item.categoryName}] - ${item.shirtSize}`
                metadata[`itemAmount${idx + 1}`] =
                  (item.isTriathlon ||
                    Object.keys(TEAM_TYPES).includes(item.teamType)) &&
                  idx > 0
                    ? ''
                    : item.isBonus
                    ? 'FREE'
                    : `PHP ${item.fee.toFixed(2)}`
                metadata[`itemTransactionFee${idx + 1}`] =
                  (item.isTriathlon ||
                    Object.keys(TEAM_TYPES).includes(item.teamType)) &&
                  idx > 0
                    ? ''
                    : item.isBonus
                    ? 'FREE'
                    : `PHP ${item.adminFee.toFixed(2)}`
              })

              const piRes: PaymentIntentResponse = await createPaymentIntent(
                newCartItems[0].eventName,
                parseInt((newShoppingCartTotal * 100).toFixed()),
                metadata,
              )

              // client: call paymongo PaymentMethod - client
              const pmRes: PaymentMethodResponse = await createPaymentMethod({
                type: values.paymentType.toLowerCase(),
                billing: {
                  address: {
                    line1: values.addressLine1,
                    city: values.city,
                    state: values.state,
                    postal_code: values.postalCode,
                    country: values.country,
                  },
                  name: values.name,
                  email: values.email,
                  phone: values.phone,
                },
                ...(values.paymentType === PAYMENT_MODES.CARD.key && {
                  details: {
                    card_number: values.cardNumber.toString(),
                    exp_month: parseInt(values.expMonth.toString()),
                    exp_year: parseInt(values.expYear.toString()),
                    cvc: values.cvc.toString(),
                  },
                }),
              })
              if ('errors' in pmRes) {
                setLoading(false)
                setPageLoading(false)
                return [
                  null,
                  { message: `Payment error: ${pmRes.errors[0].detail}` },
                ]
              }

              // save pending order
              await doCreatePendingOrder(
                cartUser,
                newCartItems,
                piRes.data.id,
                newShoppingCartTotal,
                piRes.data.attributes.status,
                values.paymentType,
                shippingPreference,
              )

              // client: call attach to paymentIntent - client
              const aRes: AttachPaymentIntentResponse =
                await attachToPaymentIntent(piRes.data.id, {
                  payment_method: pmRes.data.id,
                  client_key: piRes.data.attributes.client_key,
                  ...([
                    PAYMENT_MODES.GCASH.key,
                    PAYMENT_MODES.PAYMAYA.key,
                    PAYMENT_MODES.GRAB_PAY.key,
                  ].includes(values.paymentType) && {
                    return_url: `${window.location.origin}${process.env.REACT_APP_PAYMONGO_RETURN_URL}`,
                  }),
                })

              if ('errors' in aRes)
                return [
                  null,
                  {
                    title: 'Error encountered',
                    message:
                      'Sorry. Error encountered while processing request. Please contact admin@megatechph.com for assistance ',
                  },
                ]

              if (aRes.data.attributes.next_action === null) {
                // retrieve paymentIntent to get status
                // if status is success, then show success message
                window.location.href = `${window.location.origin}${process.env.REACT_APP_PAYMONGO_RETURN_URL}?payment_intent_id=${aRes.data.id}`
              } else {
                // redirect user to next_action.redirect
                if (
                  aRes.data.attributes.next_action.redirect?.return_url !== null
                )
                  window.location.href =
                    aRes.data.attributes.next_action.redirect.url
              }

              setLoading(false)
              setPageLoading(false)
            } catch (e) {
              errorCallback(e)
              setLoading(false)
              setPageLoading(false)
              return [null, e]
            }
          }}
          setLoading={(a: boolean) => {
            setLoading(a)
            setPageLoading(a)
          }}
        />
      </>
    )
  }

  const renderContent = () => {
    if (cartItems.length === 0)
      return (
        <Page centered>
          <EmptyState
            header={''}
            containerSx={{ textAlign: 'center' }}
            text={'Your shopping cart is empty.'}>
            <Link href="/events" sx={{ textDecoration: 'none' }}>
              <Button color="secondary" variant="contained">
                BACK to Events
              </Button>
            </Link>
          </EmptyState>
        </Page>
      )

    return (
      <>
        <div style={{ height: 48 }} />
        {loading && <OngoingPaymentNotice isFree={isFree} />}
        <Grid container spacing={5}>
          <RegistrationSummary
            shoppingCartItems={shoppingCartItems}
            shoppingCartTotal={shoppingCartTotal}
            loading={loading || pageLoading}
            checkWaiver={checkWaiver}
            checkWaiverOrg={checkWaiverOrg}
            checkAgreeVerify={checkAgreeVerify}
            handleCheck={handleCheck}
            isVirtualEvent={isVirtualEvent}
            eventWaiver={eventWaiver}
            onCancel={onCancel}
            isFree={isFree}
            handleFreeSubmit={async () => {
              setLoading(true)
              await handleFreeSubmit()
            }}
          />
          {!isFree && (
            <Grid
              sx={{ paddingTop: 0 }}
              size={{
                xs: 12,
                sm: 6,
              }}>
              {checkWaiver &&
                checkWaiverOrg &&
                checkAgreeVerify &&
                renderCheckout()}
            </Grid>
          )}
        </Grid>
      </>
    )
  }

  return <Container disableGutters>{show && renderContent()}</Container>
}

export default PaymongoCheckout
